import React, { useEffect, useState } from 'react';
import './TravelReplacementModal.css';

const DURATIONS = [10, 15, 20, 30];

export const isReplacementModeSelected = (currentMode, optionMode) => currentMode === optionMode;

const TravelReplacementModal = ({ workout, preview, exercises = [], backendService, onCreated, close, modalService }) => {
  const trackableActivities = exercises
    .filter(exercise => String(exercise?.category || '').toLowerCase() === 'cardio')
    .map(exercise => ({
      id: String(exercise?._id || exercise?.id || '').trim(),
      name: String(exercise?.name || exercise?._id || exercise?.id || '').trim(),
    }))
    .filter(exercise => exercise.id && exercise.name)
    .filter((exercise, index, all) => all.findIndex(candidate => candidate.id === exercise.id) === index)
    .sort((left, right) => left.name.localeCompare(right.name));
  const defaultTrackedActivityId = trackableActivities.find(exercise => exercise.id === 'Hike')?.id
    || trackableActivities[0]?.id
    || '';

  const [replacementMode, setReplacementMode] = useState('guided_circuit');
  const [durationMinutes, setDurationMinutes] = useState(20);
  const [lowImpact, setLowImpact] = useState(false);
  const [limitations, setLimitations] = useState('');
  const [activityExerciseId, setActivityExerciseId] = useState('');
  const [activityDurationMinutes, setActivityDurationMinutes] = useState('60');
  const [activityNotes, setActivityNotes] = useState('');
  const [existingActivityId, setExistingActivityId] = useState('');
  const [invalidFields, setInvalidFields] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const allowance = preview?.allowance;
  const existingActivities = Array.isArray(preview?.existing_activities) ? preview.existing_activities : [];
  const trackedActivity = replacementMode === 'tracked_activity';
  const existingActivity = replacementMode === 'existing_activity';
  const selectedActivityExerciseId = activityExerciseId || defaultTrackedActivityId;
  const selectedActivity = trackableActivities.find(activity => activity.id === selectedActivityExerciseId);
  const selectedExistingActivityId = existingActivityId || String(existingActivities[0]?._id || '');
  const selectedExistingActivity = existingActivities.find(activity => String(activity?._id) === selectedExistingActivityId);

  useEffect(() => {
    document.body.classList.add('travel-replacement-modal-open');
    return () => document.body.classList.remove('travel-replacement-modal-open');
  }, []);

  useEffect(() => {
    if (!submitting) return undefined;
    modalService?.disableClose?.();
    return () => modalService?.enableClose?.();
  }, [modalService, submitting]);

  const chooseMode = mode => {
    if (submitting) return;
    setReplacementMode(mode);
    setInvalidFields({});
    setError('');
  };

  const createReplacement = async () => {
    if (!workout?._id || submitting) return;

    const trackedDuration = Number(activityDurationMinutes);
    if (existingActivity && !selectedExistingActivity) {
      setInvalidFields({ existingActivity: true });
      setError('Select a completed custom activity.');
      return;
    }
    if (trackedActivity) {
      const nextInvalidFields = {
        activityExercise: !selectedActivity,
        activityDuration: !Number.isInteger(trackedDuration) || trackedDuration < 1 || trackedDuration > 1440,
      };
      if (nextInvalidFields.activityExercise || nextInvalidFields.activityDuration) {
        setInvalidFields(nextInvalidFields);
        setError('Select an activity and enter a whole-number duration from 1 to 1,440 minutes.');
        return;
      }
    }

    setSubmitting(true);
    setInvalidFields({});
    setError('');
    try {
      const result = existingActivity
        ? await backendService.attachExistingTravelReplacement(workout._id, selectedExistingActivityId)
        : await backendService.createTravelReplacement(workout._id, trackedActivity ? {
          replacement_mode: 'tracked_activity',
          activity_exercise_id: selectedActivity.id,
          duration_minutes: trackedDuration,
          activity_notes: activityNotes.trim(),
          low_impact: false,
          limitations: '',
        } : {
          duration_minutes: durationMinutes,
          low_impact: lowImpact,
          limitations: limitations.trim(),
          replacement_mode: 'guided_circuit',
          activity_exercise_id: '',
          activity_notes: '',
        });
      onCreated?.(result);
      close?.();
    } catch (requestError) {
      setError(requestError?.response?.data?.error || requestError?.message || 'Could not create the travel replacement.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="travel-replacement-modal" role="dialog" aria-modal="true" aria-labelledby="travel-replacement-title">
        <h3 id="travel-replacement-title">Replace {workout?.name || 'workout'}</h3>
        {allowance && (
          <div className="travel-replacement-balance">
            <strong>{allowance.remaining} / {allowance.total} available</strong>
            {allowance.scope === 'dojo' && <span>Established by your dojo</span>}
          </div>
        )}
        <p>
          This replacement preserves consistency credit with a guided circuit, a new tracked cardio activity, or a recently completed custom activity.
          It does not reproduce the original training effect.
        </p>

        <fieldset className="travel-replacement-mode" disabled={submitting}>
          <legend>How do you want to replace it?</legend>
          <label className={!trackedActivity && !existingActivity ? 'selected' : ''} htmlFor="travel-replacement-mode-guided">
            <input
              id="travel-replacement-mode-guided"
              type="radio"
              name="travel-replacement-mode"
              value="guided_circuit"
              checked={isReplacementModeSelected(replacementMode, 'guided_circuit')}
              onChange={() => chooseMode('guided_circuit')}
            />
            <span><strong>Build me a circuit</strong><small>Create a short bodyweight workout to complete.</small></span>
          </label>
          <label className={trackedActivity ? 'selected' : ''} htmlFor="travel-replacement-mode-tracked">
            <input
              id="travel-replacement-mode-tracked"
              type="radio"
              name="travel-replacement-mode"
              value="tracked_activity"
              checked={isReplacementModeSelected(replacementMode, 'tracked_activity')}
              onChange={() => chooseMode('tracked_activity')}
              disabled={submitting || trackableActivities.length === 0}
            />
            <span>
              <strong>Track a cardio activity</strong>
              <small>{trackableActivities.length ? 'For example, a hike, run, ride, or swim.' : 'No cardio activities are available.'}</small>
            </span>
          </label>
          <label className={existingActivity ? 'selected' : ''} htmlFor="travel-replacement-mode-existing">
            <input
              id="travel-replacement-mode-existing"
              type="radio"
              name="travel-replacement-mode"
              value="existing_activity"
              checked={isReplacementModeSelected(replacementMode, 'existing_activity')}
              onChange={() => chooseMode('existing_activity')}
              disabled={submitting || existingActivities.length === 0}
            />
            <span>
              <strong>Use an activity I already logged</strong>
              <small>{existingActivities.length
                ? 'Choose a recent completed custom activity.'
                : 'No eligible completed custom activities are available.'}</small>
            </span>
          </label>
        </fieldset>

        {existingActivity ? (
          <div className="travel-replacement-fields">
            <label htmlFor="travel-replacement-existing-activity">
              Completed custom activity
              <select
                id="travel-replacement-existing-activity"
                value={selectedExistingActivityId}
                onChange={event => setExistingActivityId(event.target.value)}
                disabled={submitting}
                aria-invalid={invalidFields.existingActivity ? 'true' : undefined}
                aria-describedby={invalidFields.existingActivity ? 'travel-replacement-error' : undefined}
              >
                {existingActivities.map(activity => {
                  const completedDate = new Date(activity.completed_at);
                  const dateLabel = Number.isNaN(completedDate.getTime())
                    ? 'Completed'
                    : completedDate.toLocaleDateString();
                  return (
                    <option key={activity._id} value={activity._id}>
                      {activity.name || 'Custom workout'} · {dateLabel}
                    </option>
                  );
                })}
              </select>
            </label>
            <div className="travel-replacement-note">
              This keeps the activity in your history and uses it once for consistency credit. Its completion record will be locked after assignment.
            </div>
          </div>
        ) : trackedActivity ? (
          <div className="travel-replacement-fields">
            <label htmlFor="travel-replacement-activity-exercise">
              Activity
              <select
                id="travel-replacement-activity-exercise"
                value={selectedActivityExerciseId}
                onChange={event => setActivityExerciseId(event.target.value)}
                disabled={submitting}
                aria-invalid={invalidFields.activityExercise ? 'true' : undefined}
                aria-describedby={invalidFields.activityExercise ? 'travel-replacement-error' : undefined}
              >
                {trackableActivities.map(activity => (
                  <option key={activity.id} value={activity.id}>{activity.name}</option>
                ))}
              </select>
            </label>
            <label htmlFor="travel-replacement-activity-duration">
              Duration (minutes)
              <input
                id="travel-replacement-activity-duration"
                type="number"
                inputMode="numeric"
                min="1"
                max="1440"
                step="1"
                value={activityDurationMinutes}
                onChange={event => setActivityDurationMinutes(event.target.value)}
                disabled={submitting}
                aria-invalid={invalidFields.activityDuration ? 'true' : undefined}
                aria-describedby={invalidFields.activityDuration ? 'travel-replacement-error' : undefined}
              />
            </label>
            <label htmlFor="travel-replacement-activity-notes">
              Notes (optional)
              <textarea
                id="travel-replacement-activity-notes"
                value={activityNotes}
                onChange={event => setActivityNotes(event.target.value)}
                maxLength={500}
                rows={3}
                disabled={submitting}
                placeholder="Route, terrain, effort, or who joined you"
              />
            </label>
            <div className="travel-replacement-note">
              Kata.fit will prefill the duration. Review its metrics and submit it by the deadline to receive consistency credit.
            </div>
          </div>
        ) : (
          <div className="travel-replacement-fields">
            <label htmlFor="travel-replacement-duration">
              Duration
              <select
                id="travel-replacement-duration"
                value={durationMinutes}
                onChange={event => setDurationMinutes(Number(event.target.value))}
                disabled={submitting}
              >
                {DURATIONS.map(minutes => <option key={minutes} value={minutes}>{minutes} minutes</option>)}
              </select>
            </label>
            <label className="travel-replacement-check" htmlFor="travel-replacement-low-impact">
              <input
                id="travel-replacement-low-impact"
                type="checkbox"
                checked={lowImpact}
                onChange={event => setLowImpact(event.target.checked)}
                disabled={submitting}
              />
              <span>Low-impact circuit</span>
            </label>
            <label htmlFor="travel-replacement-limitations">
              Equipment or movement limitations (optional)
              <textarea
                id="travel-replacement-limitations"
                value={limitations}
                onChange={event => setLimitations(event.target.value)}
                maxLength={500}
                rows={3}
                disabled={submitting}
                placeholder="For example: no jumping, sore wrist, hotel room only"
              />
            </label>
            <div className="travel-replacement-note">
              Choose Rest Day instead if you are sick, injured, or need recovery.
            </div>
          </div>
        )}

        {error && <div id="travel-replacement-error" className="travel-replacement-error" role="alert">{error}</div>}
        <div className={`travel-replacement-actions${trackedActivity ? ' tracked-activity' : ''}`}>
          <button type="button" className="btn-add" onClick={createReplacement} disabled={submitting || preview?.eligible === false}>
            {submitting
              ? 'Saving…'
              : existingActivity
                ? 'Use selected activity'
                : trackedActivity
                  ? 'Create activity replacement'
                  : 'Create circuit'}
          </button>
          <button type="button" className="btn-cancel" onClick={close} disabled={submitting}>Cancel</button>
        </div>
      </div>
  );
};

export default TravelReplacementModal;
